import React, { useCallback, useContext, useEffect, useId, useRef, useState } from 'react'
import { useIntl } from 'react-intl'

// components
import SalonsListPageHead from './components/SalonsListPageHead/SalonsListPageHead'
import SalonsView from '../../components/SalonsView/SalonsView'
import SalonsAndCitiesSearchWrap from '../../components/SalonsAndCitiesSearchWrap/SalonsAndCitiesSearchWrap'
import SalonsSeoSchema from '../../components/SalonsSeoSchema/SalonsSeoSchema'
import Filters from '../../components/Filters/Filters'
import CategoriesNavigation from './components/CategoriesNavigation/CategoriesNavigation'
import VisuallyHidden from '../../atoms/VisuallyHidden/VisuallyHidden'
import SkipLink from '../../atoms/SkipLink/SkipLink'

// types
import {
	GetSalonsDataParams,
	CategoriesResponse,
	ConfigResponse,
	SalonsResponse,
	LoadSalonsFromFiltersParams,
	SalonsListPageQuery,
	CmsSalonsListPageData,
	Currency,
	Cosmetic,
	Language,
	GetSalonsFilterCountResponse,
	PageProps,
	SalonsListPageContainerQuery
} from '../../types/types'

// styles
import * as SC from './SalonsListPageStyles'

// utils
import createPageContainer from '../../utils/createPageContainer'
import { AppContext } from '../../utils/appProvider'
import { areReservationsAllowed, PAGE_LINKS, isNil } from '../../utils/helper'
import { pushToDataLayer } from '../../utils/dataLayer'
import { getPageViewEvent, getViewItemListEvent } from '../../utils/dataLayerEvents'
import { MyLocationContext } from '../../utils/myLocationProvider'
import { shopsConfig, shopsConfigById } from '../../appDefaults'
import { ABORT_KEYS, SESSION_STORAGE_KEYS } from '../../utils/enums'
import { GET_MY_LOCATION_OPTION } from '../../components/SalonsAndCitiesSearch/SalonsAndCitiesSearch'

// hooks
import useMessages from '../../hooks/useMessages'

// types
type RouteProps = {}

type SalonsListPageData = {
	salonsData: SalonsResponse
	categoriesData: CategoriesResponse
	configData: ConfigResponse
	cmsData: CmsSalonsListPageData
	allowedReservations: boolean
	currency: Currency
	currencySecondary: Currency | undefined
	filterCountsData: GetSalonsFilterCountResponse
}

type SalonsListPageProps = PageProps<SalonsListPageData, SalonsListPageQuery, RouteProps>

// page
const SalonsListPage = (props: SalonsListPageProps) => {
	const {
		setPageData,
		query,
		setQuery,
		salonsData,
		filterCountsData,
		categoriesData,
		configData,
		allowedReservations,
		cmsData,
		currency,
		currencySecondary
	} = props

	const { salons, pagination } = salonsData
	const { messages } = useMessages()
	const { locale, formatMessage } = useIntl()
	const { apiBrowser } = useContext(AppContext)
	const { myLocation, isLoadingMyLocation } = useContext(MyLocationContext)
	const refineHeadingId = useId()
	const resultsHeadingId = useId()

	const [cosmetics, setCosmetics] = useState<Cosmetic[]>([])
	const [languages, setLanguages] = useState<Language[]>([])

	const [initialLocationBasedLoading, setInitialLocationBasedLoading] = useState(isLoadingMyLocation)
	const [areSalonsLoading, setAreSalonsLoading] = useState<boolean>(false)
	const [isButtonLoading, setIsButtonLoading] = useState<boolean>(false)
	const [initialClientDataLoading, setInitialClientDataLoading] = useState(true)
	const [filterStatusMessage, setFilterStatusMessage] = useState('')

	const isMountFetchRef = useRef(true)
	const isMyPositionKnownOnMount = useRef(!!myLocation)
	const mergeWithPreviousSalons = useRef(false)
	const wasMapViewRef = useRef(!!query.isMapView)

	const { pageTitle } = props.cmsData.metaData

	const defaultCity = configData.rolloutCountries.find((country) => country.languageCode === locale)?.capitalCity

	const MY_LOCATION_OPTION = GET_MY_LOCATION_OPTION()

	const refetchSalonsFromLoadMoreBtn = () => {
		setIsButtonLoading(true)

		// set mergeWithPreviousSalons flag to true so the currently showed salons can be merged with newly loaded salons
		mergeWithPreviousSalons.current = true

		setQuery({
			...query,
			page: query.page + 1
		})
	}

	const getLocationParams = (city: LoadSalonsFromFiltersParams['city']) => {
		const isUsingMyPosition = city?.placeID === MY_LOCATION_OPTION.value

		let lat: number | undefined
		let lon: number | undefined
		let latMy: number | undefined
		let lonMy: number | undefined
		let googlePlaceID: string | undefined

		if (myLocation || isUsingMyPosition) {
			latMy = myLocation?.latMy || city?.latitude
			lonMy = myLocation?.lonMy || city?.longitude
		}

		if (!isUsingMyPosition && city !== null) {
			lat = city?.latitude ?? query.lat
			lon = city?.longitude ?? query.lon
			googlePlaceID = city?.placeID ?? query.googlePlaceID
		}

		return { lat, lon, latMy, lonMy, googlePlaceID, isUsingMyPosition }
	}

	const updateQueryFromFilters = async (params: LoadSalonsFromFiltersParams) => {
		const {
			openingHoursStatus: openingHoursStatusParam,
			city,
			orderBy: orderByParam,
			exactRating: exactRatingParam,
			languageIDs: languageIDsParam,
			cosmeticIDs: cosmeticIDsParam,
			serviceTotalPriceFrom: serviceTotalPriceFromParam,
			serviceTotalPriceTo: serviceTotalPriceToParam,
			hasAvailableReservationSystem: hasAvailableReservationSystemParam,
			// NOTE: gift filter temporary commented out https://goodrequest.atlassian.net/browse/NOT-16945
			// hasGift: hasGiftParam,
			avResTimeSlotDayPart: avResTimeSlotDayPartParam,
			avResTimeSlotDate: avResTimeSlotDateParam,
			avResTimeSlotDateFrom: avResTimeSlotDateFromParam,
			avResTimeSlotDateTo: avResTimeSlotDateToParam
		} = params

		const openingHoursStatus = openingHoursStatusParam || query.openingHoursStatus
		const orderBy = orderByParam || query.orderBy
		const exactRating = exactRatingParam || query.exactRating
		const languageIDs = languageIDsParam || query.languageIDs
		const cosmeticIDs = cosmeticIDsParam || query.cosmeticIDs
		const avResTimeSlotDayPart = avResTimeSlotDayPartParam || query.avResTimeSlotDayPart

		// using null to purposely reset value in following params
		const serviceTotalPriceFrom = serviceTotalPriceFromParam === null ? undefined : (serviceTotalPriceFromParam ?? query.serviceTotalPriceFrom)
		const serviceTotalPriceTo = serviceTotalPriceToParam === null ? undefined : (serviceTotalPriceToParam ?? query.serviceTotalPriceTo)
		const avResTimeSlotDate = avResTimeSlotDateParam === null ? undefined : avResTimeSlotDateParam || query.avResTimeSlotDate
		const avResTimeSlotDateFrom = avResTimeSlotDateFromParam === null ? undefined : avResTimeSlotDateFromParam || query.avResTimeSlotDateFrom
		const avResTimeSlotDateTo = avResTimeSlotDateToParam === null ? undefined : avResTimeSlotDateToParam || query.avResTimeSlotDateTo
		const hasAvailableReservationSystem =
			hasAvailableReservationSystemParam !== undefined ? hasAvailableReservationSystemParam : query.hasAvailableReservationSystem
		// NOTE: gift filter temporary commented out https://goodrequest.atlassian.net/browse/NOT-16945
		// const hasGift = hasGiftParam !== undefined ? hasGiftParam : query.hasGift

		const { lat, lon, googlePlaceID } = getLocationParams(city)

		// update query
		setQuery({
			openingHoursStatus,
			page: 1,
			lat,
			lon,
			googlePlaceID,
			orderBy,
			exactRating,
			languageIDs,
			cosmeticIDs,
			serviceTotalPriceFrom,
			serviceTotalPriceTo,
			hasAvailableReservationSystem,
			// NOTE: gift filter temporary commented out https://goodrequest.atlassian.net/browse/NOT-16945
			// hasGift: hasGift || undefined,
			avResTimeSlotDayPart,
			avResTimeSlotDate,
			avResTimeSlotDateFrom,
			avResTimeSlotDateTo
		})
	}

	const fetchSalonsAndFilterCounts = useCallback(
		async (mergeWithPrevious = false) => {
			let commonParams: GetSalonsDataParams = {
				openingHoursStatus: query.openingHoursStatus,
				latMy: myLocation?.latMy,
				lonMy: myLocation?.lonMy,
				lat: query.lat,
				lon: query.lon,
				exactRating: query.exactRating,
				hasAvailableReservationSystem: query.hasAvailableReservationSystem,
				// NOTE: gift filter temporary commented out https://goodrequest.atlassian.net/browse/NOT-16945
				// hasGift: query.hasGift,
				languageIDs: query.languageIDs,
				cosmeticIDs: query.cosmeticIDs,
				orderBy: query.orderBy,
				avResTimeSlotDayPart: query.avResTimeSlotDayPart,
				avResTimeSlotDate: query.avResTimeSlotDate,
				avResTimeSlotDateFrom: query.avResTimeSlotDateFrom,
				avResTimeSlotDateTo: query.avResTimeSlotDateTo
			}

			if (!isNil(query.serviceTotalPriceFrom) || !isNil(query.serviceTotalPriceTo)) {
				commonParams = {
					...commonParams,
					serviceTotalPriceFrom: query.serviceTotalPriceFrom,
					serviceTotalPriceTo: query.serviceTotalPriceTo,
					serviceTotalPriceCurrencyCode: currency.code
				}
			}

			let latestAddedSalons: SalonsResponse['salons'] = []

			const { data, isCancelTokenError } = await setPageData(
				[
					{
						dataKey: 'salonsData',
						data: () =>
							apiBrowser.b2c.getSalonsData(
								{ ...commonParams, page: query.page },
								{ allowAbort: true, abortSignalKey: ABORT_KEYS.SALONS_ABORT_KEY }
							)
					},
					{
						dataKey: 'filterCountsData',
						data: () =>
							apiBrowser.b2c.getSalonsFilterCounts(
								{ ...commonParams, serviceTotalPriceCurrencyCode: currency.code },
								{ allowAbort: true, abortSignalKey: ABORT_KEYS.SALONS_FILTER_COUNT_ABORT_KEY }
							)
					}
				],
				{
					onSuccess: (prevData, newData) => {
						// newData.salonsData is the just-fetched page (not the merged list)
						latestAddedSalons = newData.salonsData.salons

						if (!mergeWithPrevious) {
							return newData
						}

						const mergedData = {
							...prevData,
							salonsData: {
								...prevData.salonsData,
								pagination: newData.salonsData.pagination,
								salons: [...prevData.salonsData.salons, ...newData.salonsData.salons]
							},
							filterCountsData: {
								...newData.filterCountsData
							}
						}

						return mergedData
					}
				}
			)

			if (data?.success === true) {
				// push to dataLayer
				const event = getViewItemListEvent({
					currentPage: query.page || 1,
					currentSortingOptionName: query.orderBy,
					salons: latestAddedSalons
				})
				pushToDataLayer(event)

				return {
					isCancelTokenError,
					success: true as const,
					totalCount: data.salonsData.pagination.totalCount,
					loadedCount: latestAddedSalons.length
				}
			}

			return { isCancelTokenError, success: false as const }
		},
		[
			apiBrowser,
			query.page,
			query.openingHoursStatus,
			query.orderBy,
			query.exactRating,
			query.lat,
			query.lon,
			query.languageIDs,
			query.cosmeticIDs,
			query.serviceTotalPriceFrom,
			query.serviceTotalPriceTo,
			query.hasAvailableReservationSystem,
			// NOTE: gift filter temporary commented out https://goodrequest.atlassian.net/browse/NOT-16945
			// query.hasGift,
			query.avResTimeSlotDayPart,
			query.avResTimeSlotDate,
			query.avResTimeSlotDateFrom,
			query.avResTimeSlotDateTo,
			currency.code,
			myLocation?.latMy,
			myLocation?.lonMy,
			setPageData
		]
	)

	useEffect(() => {
		/**
		 * Track map/list view for the fetch effect (skip "filtered" loading copy when returning from map).
		 * View-change announcement lives in SalonsView (polite live region + persistent toggle).
		 */
		if (query.isMapView) {
			wasMapViewRef.current = true
		}
	}, [query.isMapView])

	useEffect(() => {
		/**
		 * Fetch new data whenever filters/query change (or "load more") after the first data load.
		 * Announce loading (filter vs load-more), result count, or error via the status live region
		 * so screen readers hear the async list update while focus stays on the control.
		 */
		;(async () => {
			if (!isMountFetchRef.current && !query.isMapView) {
				const isLoadMore = mergeWithPreviousSalons.current
				const returningFromMap = wasMapViewRef.current

				if (isLoadMore) {
					setFilterStatusMessage(messages['Loading more salons'])
				} else if (!returningFromMap) {
					setFilterStatusMessage(messages['Loading filtered salons'])
				}

				setAreSalonsLoading(true)
				const result = await fetchSalonsAndFilterCounts(isLoadMore)

				if (!result.isCancelTokenError) {
					setAreSalonsLoading(false)
					setIsButtonLoading(false)

					if (result.success) {
						if (isLoadMore) {
							setFilterStatusMessage(
								formatMessage(
									{ id: '{count, plural, one {# more salon was loaded} other {# more salons were loaded}}' },
									{ count: result.loadedCount }
								)
							)
						} else if (!returningFromMap) {
							// Skip result-count after map→list; SalonsView already moved focus to the view toggle.
							setFilterStatusMessage(
								result.totalCount === 0
									? messages['No results found']
									: formatMessage({ id: '{count, plural, one {# salon found} other {# salons found}}' }, { count: result.totalCount })
							)
						}
					} else {
						setFilterStatusMessage(messages['Something went wrong. Data was not loaded.'])
					}
				}
				mergeWithPreviousSalons.current = false
				wasMapViewRef.current = false
			}
		})()
	}, [fetchSalonsAndFilterCounts, query.isMapView, messages, formatMessage])

	useEffect(() => {
		/**
		 * Salons are already fetched on the server side, so client-side fetching is not always necessary on page load.
		 * However, since we are working with `myLocation`, which is not available on the server, we need to handle location-based fetching.
		 * If the user grants permission to access their location, we want to refetch the data using the user's location.
		 * During this process, we show an initial loading state until the location is resolved, and if needed, fetch new data.
		 * If the user's location is already known from query parameters at page initialization, there is no need to show the initial loading state.
		 * The loading state is displayed over the salons fetched from the server, ensuring that the data is available in the HTML for search engine bots, while preventing a content flash for users.
		 * Do not update the filter status live region here — this is bootstrap, not a user-driven filter change.
		 */
		;(async () => {
			if (isLoadingMyLocation || !isMountFetchRef.current || query.isMapView) {
				return
			}

			let isCancelTokenError = false

			if (myLocation && !isMyPositionKnownOnMount.current) {
				isCancelTokenError = (await fetchSalonsAndFilterCounts()).isCancelTokenError
			}

			if (!isCancelTokenError) {
				setInitialLocationBasedLoading(false)
			}
			isMountFetchRef.current = false
		})()
	}, [isLoadingMyLocation, myLocation, fetchSalonsAndFilterCounts, query.isMapView])

	useEffect(() => {
		;(async () => {
			try {
				const [cosmeticsData, languagesData] = await Promise.all([apiBrowser.b2c.getCosmeticsData({}), apiBrowser.b2c.getLanguagesData()])

				setLanguages(languagesData.languages)
				setCosmetics(cosmeticsData.cosmetics)
			} finally {
				setInitialClientDataLoading(false)
			}
		})()
	}, [apiBrowser])

	// GA page view event
	useEffect(() => {
		const pageViewEvent = getPageViewEvent('salons', 'salons')
		pushToDataLayer(pageViewEvent)
	}, [])

	useEffect(() => {
		// remove helper data for GA event getViewItemListEvent from session storage when page unmounts or is refreshed
		// check getViewItemListEvent function for more info
		const onPageLeave = () => sessionStorage.setItem(SESSION_STORAGE_KEYS.IS_SALONS_SEARCH, '')

		window.addEventListener('beforeunload', onPageLeave)

		return () => {
			window.removeEventListener('beforeunload', onPageLeave)
			onPageLeave()
		}
	}, [])

	const getBreadcrumbItems = (hasSalonsBreadcrumb: boolean) => {
		const items = []

		if (hasSalonsBreadcrumb) {
			items.push({ link: PAGE_LINKS['/salons'](locale), name: messages.Salons })
		}

		items.push({ link: '', name: pageTitle })

		return items
	}

	const seoSchemaBreadcrumbItems = getBreadcrumbItems(true)
	const breadcrumbItems = getBreadcrumbItems(false)

	return (
		<>
			<SalonsSeoSchema breadcrumbItems={seoSchemaBreadcrumbItems} />
			<SalonsListPageHead pagination={pagination} metaData={props.cmsData.metaData} />
			<SC.Container>
				<SC.SalonsListPageWrapper>
					<SC.Grid>
						<SC.SalonsPageBreadcrumbs breadcrumbItems={breadcrumbItems} hasSalonsBreadcrumb />
						<SC.MainTitle>{pageTitle}</SC.MainTitle>
						<section aria-labelledby={refineHeadingId}>
							<VisuallyHidden as='h2' id={refineHeadingId}>
								{messages['Refine results']}
							</VisuallyHidden>
							<SalonsAndCitiesSearchWrap query={query} loadSalonsFromFilters={updateQueryFromFilters} currentCategory={undefined} />
							<SkipLink href='#salon-results'>{messages['Skip to salon results']}</SkipLink>
							<CategoriesNavigation categoriesData={categoriesData} query={query} />
							<Filters
								query={query}
								filterCounts={filterCountsData}
								currency={currency}
								currencySecondary={currencySecondary}
								cosmetics={cosmetics}
								languages={languages}
								loadSalonsFromFilters={updateQueryFromFilters}
								initialClientDataLoading={initialClientDataLoading}
								topLevelCategoryID={undefined}
								maxAvResTimeSlotDateRange={configData.maxAvResTimeSlotDateRange}
								lat={query.lat}
								lon={query.lon}
							/>
						</section>
						<section id='salon-results' aria-labelledby={resultsHeadingId} tabIndex={-1}>
							<VisuallyHidden as='h2' id={resultsHeadingId}>
								{messages['Salon results']}
							</VisuallyHidden>
							<VisuallyHidden as='div' role='status' aria-live='polite' aria-atomic='true'>
								{filterStatusMessage}
							</VisuallyHidden>
							<SalonsView
								salons={salons}
								pagination={pagination}
								query={query}
								setQuery={setQuery}
								loadSalonsFromLoadMoreButton={refetchSalonsFromLoadMoreBtn}
								loadSalonsFromFilters={updateQueryFromFilters}
								initialLoading={initialLocationBasedLoading}
								salonsLoading={areSalonsLoading}
								buttonLoading={isButtonLoading}
								defaultCity={defaultCity}
								allowedReservations={allowedReservations}
								currencyCode={currency.code}
								cmsData={{
									appPromoData: cmsData.appPromoData,
									smallBannerData: cmsData.salonListSmallBannerData,
									metaData: cmsData.metaData
								}}
								lat={query.lat}
								lon={query.lon}
							/>
						</section>
					</SC.Grid>
				</SC.SalonsListPageWrapper>
			</SC.Container>
		</>
	)
}

// page container
const SalonsListPageContainer = createPageContainer<SalonsListPageData, SalonsListPageContainerQuery, RouteProps>({
	identifier: 'SalonsListPage',
	PageComponent: SalonsListPage,
	initDefaultData: async ({ api, query, shopId, locale /* , isAuthorized */ }) => {
		const configData = await api.b2c.getConfigData()
		const { countryCode } = shopsConfigById[shopId]
		const localeCode = shopsConfig.find((config) => config.lang === locale)?.locale
		const countryData = configData.rolloutCountries.find((country) => country.languageCode === localeCode)
		const currencyCode = countryData?.currencyCode
		const currency = configData.systemCurrencies.find((c) => c.code === currencyCode)

		const currencyCodeSecondary = countryData?.currencyCodeSecondary
		const currencySecondary = configData.systemCurrencies.find((c) => c.code === currencyCodeSecondary)

		if (!currency) {
			throw new Error(`Currency not found for locale: ${locale}`)
		}

		const allowedReservations = areReservationsAllowed(configData.allowReservations, shopId)

		let getSalonsDataParams: GetSalonsDataParams = {
			openingHoursStatus: query.openingHoursStatus,
			latMy: query.latMy,
			lonMy: query.lonMy,
			lat: query.lat,
			lon: query.lon,
			exactRating: query.exactRating,
			languageIDs: query.languageIDs,
			hasAvailableReservationSystem: query.hasAvailableReservationSystem,
			// NOTE: gift filter temporary commented out https://goodrequest.atlassian.net/browse/NOT-16945
			// hasGift: query.hasGift,
			orderBy: query.orderBy,
			avResTimeSlotDayPart: query.avResTimeSlotDayPart,
			avResTimeSlotDate: query.avResTimeSlotDate,
			avResTimeSlotDateFrom: query.avResTimeSlotDateFrom,
			avResTimeSlotDateTo: query.avResTimeSlotDateTo
		}

		if (!isNil(query.serviceTotalPriceFrom) || !isNil(query.serviceTotalPriceTo)) {
			getSalonsDataParams = {
				...getSalonsDataParams,
				serviceTotalPriceFrom: query.serviceTotalPriceFrom,
				serviceTotalPriceTo: query.serviceTotalPriceTo,
				serviceTotalPriceCurrencyCode: currency.code
			}
		}

		const [categoriesData, salonsData, filterCountsData, cmsData] = await Promise.all([
			api.b2c.getCategoriesData(),
			api.b2c.getSalonsData({
				...getSalonsDataParams,
				page: query.page
			}),
			api.b2c.getSalonsFilterCounts({ ...getSalonsDataParams, serviceTotalPriceCurrencyCode: currency.code }),
			api.cms.getSalonsListPageCmsData({
				countryCode
			})
		])

		return {
			success: true,
			salonsData,
			categoriesData,
			configData,
			cmsData,
			allowedReservations,
			currency,
			currencySecondary,
			filterCountsData
		}
	}
})

export default SalonsListPageContainer
